import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Textarea,
} from '@heroui/react';
import React, { useMemo, useState } from 'react';

import { AlertCard } from '../../../../../../../components/AlertCard';
import { useVendorPromotionMarketingUpdate } from '../hooks/useVendorPromotionMarketingUpdate';
import { type PromotionRow, type PromotionTranslation } from '../types';

interface PromotionDisplayCopyFormProps {
  promotion: PromotionRow;
  vendorId?: string;
  /** Hands the saved record back so the list row can be replaced. */
  onSaved: (promotion: PromotionRow) => void;
}

interface DisplayCopyDraft {
  displayName: string;
  displayDescription: string;
}

type DisplayCopyDrafts = Record<string, DisplayCopyDraft>;

/** An input the operator emptied clears the override, so it becomes null. */
const normalise = (value: string): string | null => value.trim() || null;

/** Only a locale the server already overrides can be reverted. */
const hasStoredOverride = (translation: PromotionTranslation): boolean =>
  translation.displayName !== null || translation.displayDescription !== null;

const OVERRIDE_WARNING =
  'Overrides replace the ERP copy for every buyer of this distributor. The ERP ' +
  'text stays stored and Revert to ERP copy restores it.';

interface LocaleState {
  label: string;
  tone: 'warning' | 'primary' | 'default';
}

const UNSAVED_STATE: LocaleState = {
  label: 'Unsaved changes',
  tone: 'warning',
};
const OVERRIDE_STATE: LocaleState = {
  label: 'Override saved',
  tone: 'primary',
};
const ERP_STATE: LocaleState = { label: 'Uses ERP copy', tone: 'default' };

const toDrafts = (translations: PromotionTranslation[]): DisplayCopyDrafts =>
  translations.reduce<DisplayCopyDrafts>(
    (drafts, translation) => ({
      ...drafts,
      [translation.locale]: {
        displayName: translation.displayName ?? '',
        displayDescription: translation.displayDescription ?? '',
      },
    }),
    {}
  );

/**
 * The Choco-side override of a promotion's copy, one block per locale. Only the
 * locales the operator changed are sent, so a save leaves the rest untouched.
 */
export const PromotionDisplayCopyForm: React.FC<
  PromotionDisplayCopyFormProps
> = ({ promotion, vendorId, onSaved }) => {
  // A different row remounts this form through the key the modal sets, so the
  // drafts are read from the promotion once.
  const [drafts, setDrafts] = useState<DisplayCopyDrafts>(() =>
    toDrafts(promotion.translations)
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [revertingLocale, setRevertingLocale] = useState<string | null>(null);
  const { save, isSaving } = useVendorPromotionMarketingUpdate({ vendorId });

  const changedTranslations = useMemo(
    () =>
      promotion.translations
        .map((translation) => {
          const draft = drafts[translation.locale];
          if (!draft) {
            return null;
          }
          const displayName = normalise(draft.displayName);
          const displayDescription = normalise(draft.displayDescription);
          const isChanged =
            displayName !== translation.displayName ||
            displayDescription !== translation.displayDescription;

          return isChanged
            ? { locale: translation.locale, displayName, displayDescription }
            : null;
        })
        .filter(
          (
            translation
          ): translation is {
            locale: string;
            displayName: string | null;
            displayDescription: string | null;
          } => translation !== null
        ),
    [promotion.translations, drafts]
  );

  // The warning is for an override already stored, and for one being typed.
  const hasOverrideCopy = useMemo(
    () =>
      promotion.translations.some((translation) => {
        const draft = drafts[translation.locale];

        return (
          hasStoredOverride(translation) ||
          normalise(draft?.displayName ?? '') !== null ||
          normalise(draft?.displayDescription ?? '') !== null
        );
      }),
    [promotion.translations, drafts]
  );

  const changedLocales = useMemo(
    () => new Set(changedTranslations.map((translation) => translation.locale)),
    [changedTranslations]
  );

  const localeState = (translation: PromotionTranslation): LocaleState => {
    if (changedLocales.has(translation.locale)) {
      return UNSAVED_STATE;
    }

    return hasStoredOverride(translation) ? OVERRIDE_STATE : ERP_STATE;
  };

  const updateDraft = (
    locale: string,
    field: keyof DisplayCopyDraft,
    value: string
  ): void => {
    setIsSaved(false);
    setDrafts((previous) => ({
      ...previous,
      [locale]: { ...previous[locale], [field]: value },
    }));
  };

  const handleSave = async (): Promise<void> => {
    setErrorMessage(null);
    const sent = changedTranslations;
    const sentByLocale = new Map(
      sent.map((translation) => [translation.locale, translation] as const)
    );
    const result = await save(promotion.id, sent);

    if (result.kind === 'success') {
      // A locale still showing what was sent is restated from the server
      // answer. One the operator retyped during the request keeps its draft.
      setDrafts((previous) => {
        const restated = { ...previous };

        result.promotion.translations.forEach((translation) => {
          const sentTranslation = sentByLocale.get(translation.locale);
          const draft = previous[translation.locale];
          if (!sentTranslation || !draft) {
            return;
          }
          const isUntouched =
            normalise(draft.displayName) === sentTranslation.displayName &&
            normalise(draft.displayDescription) ===
              sentTranslation.displayDescription;
          if (isUntouched) {
            restated[translation.locale] = {
              displayName: translation.displayName ?? '',
              displayDescription: translation.displayDescription ?? '',
            };
          }
        });

        return restated;
      });
      setIsSaved(true);
      onSaved(result.promotion);
      return;
    }
    setErrorMessage(result.message);
  };

  /**
   * Drops both overrides of one locale, so the buyer reads the ERP copy again.
   * The reset entry goes on its own, which keeps the drafts of other locales.
   */
  const handleRevert = async (locale: string): Promise<void> => {
    setErrorMessage(null);
    setIsSaved(false);
    setRevertingLocale(locale);
    const draftAtRequest = drafts[locale];
    const result = await save(promotion.id, [{ locale, reset: true }]);
    setRevertingLocale(null);

    if (result.kind === 'success') {
      const reverted = result.promotion.translations.find(
        (translation) => translation.locale === locale
      );

      // An edit the operator typed during the request keeps its draft, the way
      // a save that lands late does.
      setDrafts((previous) => {
        const draft = previous[locale];
        const isUntouched =
          draft?.displayName === draftAtRequest?.displayName &&
          draft?.displayDescription === draftAtRequest?.displayDescription;

        return isUntouched
          ? {
              ...previous,
              [locale]: {
                displayName: reverted?.displayName ?? '',
                displayDescription: reverted?.displayDescription ?? '',
              },
            }
          : previous;
      });
      onSaved(result.promotion);
      return;
    }
    setErrorMessage(result.message);
  };

  if (promotion.translations.length === 0) {
    return (
      <div>
        <h4 className="mb-2 text-sm font-semibold text-foreground">
          Display copy
        </h4>
        <p className="text-sm text-default-400">
          This promotion has no copy to override.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-1 text-sm font-semibold text-foreground">
        Display copy
      </h4>
      <p className="mb-3 text-sm text-default-500">
        What the buyer sees instead of the copy the ERP sent. Empty a field to
        drop the override.
      </p>

      {hasOverrideCopy && (
        <AlertCard
          className="mb-3 shadow-none"
          type="warning"
          message={OVERRIDE_WARNING}
        />
      )}

      <div className="space-y-4">
        {promotion.translations.map((translation) => {
          const state = localeState(translation);

          return (
            <Card
              key={translation.locale}
              shadow="none"
              className="rounded-xl border border-default-200"
              role="group"
              aria-label={`Display copy for ${translation.locale}`}
            >
              <CardHeader className="flex flex-wrap items-center gap-2 px-4 py-3">
                <Chip size="sm" variant="flat">
                  {translation.locale}
                </Chip>
                <Chip size="sm" variant="flat" color={state.tone}>
                  {state.label}
                </Chip>
                <Button
                  className="ml-auto"
                  size="sm"
                  variant="flat"
                  aria-label={`Revert to ERP copy (${translation.locale})`}
                  onPress={() => {
                    handleRevert(translation.locale);
                  }}
                  isLoading={revertingLocale === translation.locale}
                  isDisabled={isSaving || !hasStoredOverride(translation)}
                >
                  Revert to ERP copy
                </Button>
              </CardHeader>

              {/* The ERP copy and the override read side by side, so an
                  operator sees what the override is replacing. */}
              <CardBody className="grid grid-cols-1 gap-4 p-4 pt-0 md:grid-cols-2">
                <div className="min-w-0 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-default-400">
                    ERP copy
                  </p>
                  <div>
                    <p className="text-xs font-medium text-default-500">Name</p>
                    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-default-700">
                      {translation.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-default-500">
                      Description
                    </p>
                    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-default-700">
                      {translation.description}
                    </p>
                  </div>
                </div>

                {/* Wider spacing than the ERP column, because an outside
                    label needs room above its field. */}
                <div className="min-w-0 space-y-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-default-400">
                    Choco override
                  </p>
                  <Input
                    label={`Display name (${translation.locale})`}
                    labelPlacement="outside"
                    variant="bordered"
                    placeholder="Use the ERP name"
                    value={drafts[translation.locale]?.displayName ?? ''}
                    onValueChange={(value) =>
                      updateDraft(translation.locale, 'displayName', value)
                    }
                  />
                  <Textarea
                    label={`Display description (${translation.locale})`}
                    labelPlacement="outside"
                    variant="bordered"
                    placeholder="Use the ERP description"
                    minRows={2}
                    value={drafts[translation.locale]?.displayDescription ?? ''}
                    onValueChange={(value) =>
                      updateDraft(
                        translation.locale,
                        'displayDescription',
                        value
                      )
                    }
                  />
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>

      {errorMessage && (
        <AlertCard
          className="mt-4"
          type="error"
          title="Could not save the display copy"
          message={errorMessage}
        />
      )}

      <div className="mt-4 flex items-center justify-end gap-3">
        {/* An edit made during the request leaves something to save, so the
            confirmation waits rather than covering it. */}
        {isSaved && !errorMessage && changedTranslations.length === 0 && (
          <span className="text-sm text-success-600">Display copy saved.</span>
        )}
        <Button
          color="primary"
          onPress={() => {
            handleSave();
          }}
          isLoading={isSaving}
          isDisabled={isSaving || changedTranslations.length === 0}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
